"use client";

import { useEffect, useState, type Dispatch, type SetStateAction } from "react";

const PREFIX = "fyt-draft:";

/**
 * State local « brouillon » persistant : se comporte comme useState, mais
 * sauvegarde la valeur dans sessionStorage à chaque changement et la restaure
 * au montage. Permet de retrouver une sélection en revenant en arrière SANS
 * avoir validé (« autosave partout », cf. feedback Djam).
 *
 * - sessionStorage → nettoyé à la fermeture de l'onglet (pas de brouillon qui traîne).
 * - Hydratation en effet (pas en init) pour éviter tout mismatch SSR/CSR.
 */
export function useStepDraft<T>(key: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const storageKey = PREFIX + key;
  const [value, setValue] = useState<T>(initial);
  // ⚠️ `useState` et PAS `useRef` : avec un ref, le drapeau passait à true dans
  // le même commit que la lecture, donc l'effet d'écriture ci-dessous sauvegardait
  // la valeur INITIALE par-dessus le brouillon avant même que le `setValue` de
  // restauration soit appliqué. En dev (StrictMode rejoue les effets) le brouillon
  // était définitivement écrasé — invisible tant qu'un écran ne relisait que ce
  // qu'il avait lui-même écrit, mais fatal dès qu'un AUTRE écran relit la clé
  // (rubrique « Occasion » : réponses saisies en E2, enregistrées en E4).
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw !== null) setValue(JSON.parse(raw) as T);
    } catch {
      /* sessionStorage indispo / JSON invalide → on garde la valeur initiale */
    }
    setHydrated(true);
  }, [storageKey]);

  useEffect(() => {
    if (!hydrated) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      /* quota / indispo → sans gravité */
    }
  }, [storageKey, value, hydrated]);

  return [value, setValue];
}

/** Efface les brouillons dont la clé commence par `keyPrefix` (ex. à la fin de l'onboarding). */
export function clearStepDrafts(keyPrefix: string): void {
  try {
    const full = PREFIX + keyPrefix;
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(full)) sessionStorage.removeItem(k);
    }
  } catch {
    /* indispo → no-op */
  }
}
